import { useMemo, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import ExpandableSection from "@components/animations/ExpandableSection";
import TertiaryButton from "@components/buttons/TertiaryButton";
import ArchiveSegment from "@components/carePlan/ArchiveSegment";
import styles from "@components/carePlan/CarePlan.module.css";
import CarePlanEmpty from "@components/carePlan/CarePlanEmpty";
import ExpertService from "@components/carePlan/ExpertService";
import TaskForm from "@components/forms/TaskForm";
import Section from "@components/layout/Section";
import Modal, { ModalElement } from "@components/Modal";
import ScheduleConsultLink from "@components/ScheduleConsultLink";
import InternalLink from "@components/typography/InternalLink";
import useMyClient from "@hooks/useMyClient";
import useOpener from "@hooks/useOpener";
import { GetJourneyWithPlanQuery, GoalStatusEnum, TaskFragment } from "@typing/Generated";
import { orderedTasks } from "@utils/modelUtils";
import { journeyRoute } from "@utils/routeUtils";

type Journey = GetJourneyWithPlanQuery["journey"];

type Task = TaskFragment;

type Props = {
  journey: Journey;
};

const CarePlanServices = ({ journey }: Props) => {
  const myClient = useMyClient();
  const taskOpener = useOpener();

  const expandedOpener = useOpener();
  const taskModalRef = useRef<ModalElement>(null);

  const [taskBeingEdited, setTaskBeingEdited] = useState<Task | null>();
  const onTaskEdit = (task: Task) => {
    setTaskBeingEdited(task);
    taskOpener.open();
  };

  const intl = useIntl();
  const [archived, setArchived] = useState(false);

  const tasks = useMemo(() => {
    if (journey.carePlanPublishedAt) {
      return orderedTasks(journey.goals.flatMap(g => g.tasks)).filter(
        task =>
          (task.goal?.archived === archived || (!task.goal && !archived)) && task.goal?.status === GoalStatusEnum.ACTIVE
      );
    } else {
      return [];
    }
  }, [archived, journey.carePlanPublishedAt, journey.goals]);

  return (
    <div>
      <Section
        color="primary-500"
        descriptionKey="pages.carePlan.base.expertServices.description"
        icon="logoIcon"
        titleKey="pages.carePlan.base.expertServices.title"
      >
        <ArchiveSegment
          count={
            journey.carePlanPublishedAt
              ? journey.goals.filter(goal => goal.status === GoalStatusEnum.ACTIVE).flatMap(g => g.tasks).length -
                tasks.length
              : 0
          }
          setValue={setArchived}
          value={archived}
        />
        {tasks.length > 0 ? (
          <div>
            <div className={styles.tasks}>
              {tasks.slice(0, 4).map(task => (
                <ExpertService handleEdit={onTaskEdit} journey={journey} key={task.id} task={task} />
              ))}
            </div>

            <div>
              <ExpandableSection isOpen={expandedOpener.isOpen} key="tasks-rest">
                <div className={styles.tasks + " " + styles.topSpacing}>
                  {tasks.slice(4).map(task => (
                    <ExpertService handleEdit={onTaskEdit} journey={journey} key={task.id} task={task} />
                  ))}
                </div>
              </ExpandableSection>
            </div>

            {tasks.length > 4 && (
              <div className={"align-center" + " " + styles.topSpacing}>
                <TertiaryButton
                  icon={expandedOpener.isOpen ? "chevronUp" : "chevronDown"}
                  iconPosition="end"
                  onClick={expandedOpener.toggle}
                  textKey={
                    expandedOpener.isOpen ? "pages.carePlan.base.goals.showLess" : "pages.carePlan.base.goals.showMore"
                  }
                />
              </div>
            )}
          </div>
        ) : (
          <CarePlanEmpty
            description={
              intl.formatMessage(
                { id: "pages.carePlan.base.expertServices.emptyDescription" },
                {
                  message: (
                    <InternalLink to={journeyRoute({ journeyId: journey.id, linkAfterJourney: "/messages", myClient })}>
                      <FormattedMessage id="pages.carePlan.base.expertServices.message" />
                    </InternalLink>
                  ),
                  schedule: (
                    <ScheduleConsultLink
                      journeyId={journey.id}
                      translationId="pages.carePlan.base.expertServices.schedule"
                    />
                  )
                }
              ) as string
            }
          />
        )}
      </Section>
      <Modal
        opener={taskOpener}
        ref={taskModalRef}
        title={intl.formatMessage({
          id: taskBeingEdited ? "models.tasks.editTask" : "models.tasks.addTask"
        })}
      >
        {({ modalDisplayed }) => (
          <TaskForm
            autofocus={modalDisplayed}
            clientVisibleTaskForm
            journey={journey}
            onCancel={taskOpener.close}
            onSuccess={taskOpener.close}
            primaryExpertId={journey.primaryExpert?.id}
            task={taskBeingEdited}
          />
        )}
      </Modal>
    </div>
  );
};

export default CarePlanServices;
